import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { Connector } from '@domain/connection/models/Connector';
import { ClientType } from '@domain/connection/models/Connector';
import { ShopifySettings } from '@domain/connection/models/ConnectionSettings';
import { useFullSync } from '@features/connections/generic/hooks/useFullSync';

export const useShopifyConnector = () => {
    const { data: connector, isLoading, error } = useQuery<Connector | undefined>({
        queryKey: ['shopify-connector'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.find(connector => connector.type === ClientType.SHOPIFY);
        }
    });

    return {
        connector,
        isLoading,
        error
    };
};

export const useShopifyConnections = () => {
    const { data: connections, isLoading, error } = useQuery<Connection[]>({
        queryKey: ['shopify-connections'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnections();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.filter(connection => connection.connector.type === ClientType.SHOPIFY);
        }
    });

    return {
        connections: connections || [],
        isLoading,
        error
    };
};

export const useShopifySync = () => {
    const { triggerFullSync, isSyncing } = useFullSync('shopify');

    const syncConnection = (connection: Connection) =>
        triggerFullSync({
            connectionId: connection.id,
            connectionName: connection.name,
            syncFn: (connectionId) => container.shopifyConnectorRepository.syncFull(connectionId),
        });

    const { mutateAsync: syncProducts } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.shopifyConnectorRepository.syncProducts(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    const { mutateAsync: syncOrders } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.shopifyConnectorRepository.syncOrders(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    const { mutateAsync: syncInventory } = useMutation({
        mutationFn: async (connectionId: string) => {
            const result = await container.shopifyConnectorRepository.syncInventory(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
    });

    return {
        syncConnection,
        syncProducts,
        syncOrders,
        syncInventory,
        isSyncing
    };
};

export const useShopifySettings = (connectionId: string) => {
    const queryClient = useQueryClient();

    const { data: settingsResponse, isLoading } = useQuery<ShopifySettings>({
        queryKey: ['shopify-settings', connectionId],
        queryFn: async () => {
            const result = await container.shopifyConnectorRepository.getSettings(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!connectionId,
    });

    const { mutateAsync: updateSettings, isPending: isUpdating } = useMutation({
        mutationFn: async (settings: Partial<ShopifySettings>) => {
            const result = await container.shopifyConnectorRepository.updateSettings(connectionId, settings);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shopify-settings', connectionId] });
        },
    });

    return {
        settingsResponse,
        isLoading,
        updateSettings,
        isUpdating,
    };
};
